import { Typography } from '@mui/material'
import { RpcError } from '@protobuf-ts/runtime-rpc'
import { C1Button } from 'c1ui'
import { C1Dialog, C1DialogActions, C1DialogContent, C1DialogTitle } from 'components/core/dialog/C1Dialog'
import { ClosableProps } from 'components/no-ui/context/modal-provider'
import { useAlert } from 'hooks/useAlert'
import { AppUserValue } from 'lib/actions/apps/app-users'
import { AppUserResponsiblePartyServiceSetRequest } from 'pbts/c1/api/app/v1/app_user'
import React, { FC, useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { SetAppUserIdentityApi } from 'redux_toolkit/app-users'

export type ClearIdentityModalProps = {
    appUser: AppUserValue
} & ClosableProps

export const ClearIdentityModal: FC<ClearIdentityModalProps> = ({ close, appUser }) => {
    const toast = useAlert()
    const intl = useIntl()
    const { mutate: clearIdentity } = SetAppUserIdentityApi()

    const handleSave = useCallback((): void => {
        clearIdentity(
            AppUserResponsiblePartyServiceSetRequest.create({
                appId: appUser.appId,
                appUserId: appUser.id,
            }),
        )
            .then(() => {
                toast(
                    intl.formatMessage({
                        id: 'app_user_identity_cleared',
                        defaultMessage: 'Account owner cleared',
                    }),
                    'success',
                )
                close()
            })
            .catch((err) =>
                toast(
                    intl.formatMessage(
                        {
                            id: 'app_user_type_clear_error',
                            defaultMessage: 'Error clearing account owner. {code}: {msg}',
                        },
                        {
                            code: (err as RpcError).code,
                            msg: (err as RpcError).message,
                        },
                    ),
                    'error',
                ))

        close()
    }, [appUser.appId, appUser.id, clearIdentity, close, intl, toast])

    return (
        <C1Dialog open fullWidth onClose={close}>
            <C1DialogTitle>
                <FormattedMessage id="component_clear_identity_form_title" defaultMessage="Clear account owner" />
            </C1DialogTitle>
            <C1DialogContent>
                <Typography>
                    <FormattedMessage
                        id="component_clear_identity_form_description"
                        defaultMessage="Clear the current account owner."
                    />
                </Typography>
            </C1DialogContent>
            <C1DialogActions>
                <C1Button onClick={close} variant="outlined">
                    <FormattedMessage id="component_reset_identity_form_cancel" defaultMessage="Cancel" />
                </C1Button>

                <C1Button onClick={handleSave} color="primary" variant="contained">
                    <FormattedMessage id="component_clear_identity_form_submit" defaultMessage="Clear account owner" />
                </C1Button>
            </C1DialogActions>
        </C1Dialog>
    )
}
